export interface ThreatGroupTelemetryCandidate {
    id: string;
    date: string;
    location: string;
    countryCode: string;
    sourceUrl: string;
    actor1: string;
    actor2: string;
    eventCode: string;
    lat?: number;
    lng?: number;
}

export interface ThreatGroupTelemetry {
    status: 'available' | 'empty' | 'failed';
    aliases: string[];
    startDate: string;
    endDate: string;
    candidates: ThreatGroupTelemetryCandidate[];
    truncated: boolean;
    limitations: string;
}

export const httpSource = (value: unknown): value is string => {
    try { return typeof value === 'string' && ['http:', 'https:'].includes(new URL(value).protocol); }
    catch { return false; }
};

export function threatGroupTelemetryQuery(groupName: string, aliases: string[] = [], now = new Date()) {
    const endDate = now.toISOString().slice(0, 10);
    const startDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 11, 1)).toISOString().slice(0, 10);
    const names = [...new Set([groupName, ...aliases].filter(value => typeof value === 'string')
        .map(value => value.trim()).filter(value => value.length >= 2 && value.length <= 120))].slice(0, 9);
    const terms = [...new Set(names.map(value => value.toLowerCase().replace(/[^a-z0-9]/g, '')).filter(value => value.length >= 2))];
    if (!terms.length)
        throw new Error('No usable actor names for telemetry research.');
    return { aliases: names, startDate, endDate, options: {
        params: { terms, startDate, endDate }, maximumBytesBilled: '100000000000', jobTimeoutMs: 30000,
        query: `WITH actor_events AS (SELECT CAST(GLOBALEVENTID AS STRING) AS id, CAST(SQLDATE AS STRING) AS date,
            ActionGeo_FullName AS location, ActionGeo_CountryCode AS country_code,
            SOURCEURL AS source_url, Actor1Name AS actor1, Actor2Name AS actor2,
            EventCode AS event_code, ActionGeo_Lat AS lat, ActionGeo_Long AS lng
        FROM \`gdelt-bq.gdeltv2.events_partitioned\`
        WHERE _PARTITIONTIME >= TIMESTAMP(@startDate)
            AND _PARTITIONTIME < TIMESTAMP(DATE_ADD(DATE(@endDate), INTERVAL 1 DAY))
            AND SAFE.PARSE_DATE('%Y%m%d', CAST(SQLDATE AS STRING)) BETWEEN DATE(@startDate) AND DATE(@endDate)
            AND EventRootCode IN ('18', '19', '20')
            AND EXISTS (SELECT 1 FROM UNNEST(@terms) AS term
                WHERE STRPOS(REGEXP_REPLACE(LOWER(COALESCE(Actor1Name, '')), r'[^a-z0-9]', ''), term) > 0
                    OR STRPOS(REGEXP_REPLACE(LOWER(COALESCE(Actor2Name, '')), r'[^a-z0-9]', ''), term) > 0)
        QUALIFY ROW_NUMBER() OVER (PARTITION BY GLOBALEVENTID ORDER BY NumSources DESC) = 1)
        SELECT *, COUNT(*) OVER (PARTITION BY SUBSTR(date, 1, 6)) AS month_total
        FROM actor_events
        QUALIFY ROW_NUMBER() OVER (PARTITION BY SUBSTR(date, 1, 6) ORDER BY date DESC, id) <= 40
        ORDER BY date DESC, id
        LIMIT 481`
    } };
}

export function readThreatGroupTelemetry(rows: any[], query: ReturnType<typeof threatGroupTelemetryQuery>): ThreatGroupTelemetry {
    const candidates: ThreatGroupTelemetryCandidate[] = [];
    for (const row of rows.slice(0, 500)) {
        const raw = String(row.date || '');
        const date = /^\d{8}$/.test(raw) ? `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6)}` : '';
        if (!row.id || !date || !Number.isFinite(Date.parse(date)) || new Date(date).toISOString().slice(0, 10) !== date
            || date < query.startDate || date > query.endDate || !httpSource(row.source_url))
            continue;
        candidates.push({ id: String(row.id), date, location: String(row.location || ''),
            countryCode: String(row.country_code || ''), sourceUrl: row.source_url,
            actor1: String(row.actor1 || ''), actor2: String(row.actor2 || ''), eventCode: String(row.event_code || ''),
            ...(typeof row.lat === 'number' && typeof row.lng === 'number' ? { lat: row.lat, lng: row.lng } : {}) });
    }
    return { status: candidates.length ? 'available' : 'empty', aliases: query.aliases,
        startDate: query.startDate, endDate: query.endDate, candidates, truncated: rows.some(row => Number(row.month_total) > 40),
        limitations: 'GDELT coded records are source leads, not independently confirmed incidents or complete conflict totals.'
            + (rows.some(row => Number(row.month_total) > 40) ? ' Candidate retrieval is capped at 40 records per month; coverage is partial.' : '') };
}

export function threatGroupIncidentIssues(activities: any, telemetry: ThreatGroupTelemetry, sources: any[]): string[] {
    const issues: string[] = [];
    const urls = new Set([...telemetry.candidates.map(item => item.sourceUrl), ...sources.map(source => source.web?.uri).filter(httpSource)]);
    for (let index = 0; index < (Array.isArray(activities) ? activities.length : 0); index++) {
        const item = activities[index];
        if (!urls.has(item?.sourceUrl) || !normalizeThreatGroupIncidents([item], 'Global', telemetry.startDate, telemetry.endDate).length)
            issues.push(`recentActivities[${index}] needs a valid occurrence date within the research window, a substantive description, and an exact sourceUrl from retrieved telemetry or search grounding; omit unverifiable events`);
        if (item?.telemetryEventId && !telemetry.candidates.some(lead => lead.id === item.telemetryEventId && lead.sourceUrl === item.sourceUrl))
            issues.push(`recentActivities[${index}].telemetryEventId must identify its actual supplied source lead`);
    }
    return issues;
}

export function normalizeThreatGroupIncidents(activities: any, focusCountry: string, startDate: string, endDate: string) {
    const incidents: any[] = [];
    const seen = new Set<string>();
    const normalize = (value: string) => value.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
    for (const item of Array.isArray(activities) ? activities : []) {
        const date = typeof item?.date === 'string' ? item.date.trim() : '';
        if (!/^\d{4}-(?:0[1-9]|1[0-2])(?:-(?:0[1-9]|[12]\d|3[01]))?$/.test(date) || date < startDate.slice(0, date.length) || date > endDate.slice(0, date.length)
            || !httpSource(item.sourceUrl) || typeof item.description !== 'string' || !item.description.trim())
            continue;
        if (date.length === 10 && (!Number.isFinite(Date.parse(date)) || new Date(date).toISOString().slice(0, 10) !== date))
            continue;
        const country = typeof item.country === 'string' ? item.country.trim() : '';
        const key = item.telemetryEventId ? `id:${item.telemetryEventId}`
            : [date, normalize(String(item.location || country)), normalize(item.description)].join('|');
        if (seen.has(key))
            continue;
        seen.add(key);
        incidents.push({ ...item, date, country,
            scope: country && focusCountry !== 'Global' ? normalize(country) === normalize(focusCountry) ? 'local' : 'theater' : 'unknown' });
    }
    return incidents;
}
